import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { setCookie } from '../../utils/cookiesHelper'
import { applyAnalyticsConsent } from '../../utils/firebase'
import {
  ANALYTICS_FIELD,
  CONSENT_COOKIE_KEY,
  COOKIE_CONSENT_CHANGED_EVENT,
  getDeviceConsentKey,
  getSavedPreferences
} from '../../utils/consentPreferences'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'
import { chevronDown } from 'shared-ui/assets'
import { useFetchCookieConsent, useUpdateCookieConsent } from '../../hooks/useSettings'
import { COOKIE_CONSENT } from 'shared-ui/utils/constants'
import { useLanguage } from 'shared-ui/utils/languageProvider'

const COOKIE_PREFERENCES_EVENT = 'open-cookie-preferences'
const STRICTLY_NECESSARY_FIELD = 'strictly_necessary'

const BannerWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #2e5b1a, #1e6f4f, #0b4f6c);
  z-index: 99999;
  color: #ffffff;
  text-align: left;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (min-width: 768px) {
    padding: 16px 80px;
  }
`

const BannerTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`

const BannerDescription = styled.p`
  color: #d6e2e8;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const PrivacyLink = styled.a`
  color: #00c8ff;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }
`

const TopButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: stretch;
  @media (min-width: 768px) {
    width: auto;
  }
`

const AcceptButton = styled.button`
  background: #00b8f8;
  color: #fff;
  border: none;
  border-radius: 28px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  flex: 1;
  white-space: nowrap;
  @media (min-width: 768px) {
    flex: unset;
    font-size: 14px;
    padding: 12px 22px;
  }
`

const RejectButton = styled.button`
  background: transparent;
  color: #00c8ff;
  border: 1.5px solid #00c8ff;
  border-radius: 28px;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  flex: 1;
  white-space: nowrap;
  @media (min-width: 768px) {
    flex: unset;
    font-size: 14px;
    padding: 12px 22px;
  }
`

const ManageButton = styled.button`
  background: transparent;
  border: none;
  color: #00c8ff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  width: 100%;
  @media (min-width: 768px) {
    border: 1.5px solid #00c8ff;
    border-radius: 28px;
    padding: 12px 22px;
    width: auto;
    white-space: nowrap;
  }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: #050505;
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    background: rgba(0, 0, 0, 0.65);
    flex-direction: row;
    justify-content: flex-end;
    align-items: stretch;
  }
`

const PrefsPanel = styled.div`
  background: #050505;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-align: left;
  @media (min-width: 768px) {
    width: 420px;
    max-height: 100vh;
  }
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 8px;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
  @media (min-width: 768px) {
    padding: 0 28px 8px;
  }
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 20px;
  flex-shrink: 0;
  background: #050505;
  @media (min-width: 768px) {
    padding: 28px 28px 20px;
  }
`

const BackArrow = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  @media (min-width: 768px) {
    display: none;
  }
`

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  margin-left: auto;
  @media (min-width: 768px) {
    display: flex;
    align-items: center;
  }
`

const PanelTitle = styled.h2`
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`

const PanelDescription = styled.p`
  color: #aaaaaa;
  font-size: 13px;
  line-height: 1.7;
  margin: 0 0 24px 0;
`

const CategoryRow = styled.div`
  border-top: 1px solid #000000;
  padding: 14px 0;
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 8px;
`

const CategoryLabel = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  text-align: left;
`

const DisabledToggle = styled.span`
  pointer-events: none;
  opacity: 0.5;
  flex-shrink: 0;
`

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transform: ${({ $open }) => $open ? 'rotate(180deg) scale(1.3)' : 'rotate(0deg) scale(1.3)'};
  transition: transform 0.2s ease;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const CategoryContent = styled.div`
  color: #888888;
  font-size: 13px;
  line-height: 1.7;
  margin-top: 10px;
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`

const ConfirmButtonWrapper = styled.div`
  flex-shrink: 0;
  background: #050505;
  padding: 16px 16px 24px;
  display: flex;
  justify-content: flex-start;
  @media (min-width: 768px) {
    padding: 16px 28px 28px;
  }
`

const ConfirmButton = styled.button`
  background: #00b8f8;
  color: #ffffff;
  border: none;
  border-radius: 100px;
  padding: 10px 32px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (min-width: 768px) {
    width: auto;
    min-width: 192px;
  }
`

function buildDescription(template) {
  if (!template) return null

  const privacyText = COOKIE_CONSENT.PRIVACY_POLICY_LINK || 'Privacy Policy'
  const regex = new RegExp(`(${privacyText})`, 'i')
  const parts = template.split(regex)
  if (parts.length >= 3) {
    return (
      <span>
        {parts[0]}
        <PrivacyLink href="/privacy" rel="noopener noreferrer">
          {privacyText}
        </PrivacyLink>
        {parts[2]}
      </span>
    )
  }

  return <span>{template}</span>
}

function CategoryItem({ label, field, content, isLocked, value, onToggle }) {
  const [open, setOpen] = useState(false)

  return (
    <CategoryRow>
      <CategoryHeader onClick={() => setOpen((p) => !p)}>
        <ChevronIcon $open={open}>
          <img src={chevronDown} alt="chevron" />
        </ChevronIcon>
        <CategoryLabel>{label}</CategoryLabel>
        {isLocked ? (
          <DisabledToggle>
            <ToggleSwitch isEnabled={true} onToggle={() => {}} />
          </DisabledToggle>
        ) : (
          <span onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch isEnabled={value} onToggle={() => onToggle(field)} />
          </span>
        )}
      </CategoryHeader>
      <CategoryContent $open={open}>{content}</CategoryContent>
    </CategoryRow>
  )
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [showPrefs, setShowPrefs] = useState(false)
  const [prefs, setPrefs] = useState({})

  useLanguage()
  const { data: consentData } = useFetchCookieConsent()
  const { mutateAsync: updateConsent } = useUpdateCookieConsent()

  useEffect(() => {
    const saved = getSavedPreferences()
    if (saved) {
      setPrefs(saved)
      return
    }

    setVisible(true)
  }, [])

  useEffect(() => {
    if (!consentData?.consent?.length) return
    const saved = getSavedPreferences()
    const initial = {}
    consentData.consent.forEach((c) => {
      initial[c.field] =
        c.field === STRICTLY_NECESSARY_FIELD
          ? true
          : (saved?.[c.field] ?? c.isEnabled !== false)
    })
    setPrefs(initial)
  }, [consentData])

  useEffect(() => {
    const openPreferences = () => {
      const saved = getSavedPreferences()
      if (saved) setPrefs(saved)
      setVisible(false)
      setShowPrefs(true)
    }

    window.addEventListener(COOKIE_PREFERENCES_EVENT, openPreferences)
    window.openCookiePreferences = openPreferences

    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_EVENT, openPreferences)
      if (window.openCookiePreferences === openPreferences) {
        window.openCookiePreferences = undefined
      }
    }
  }, [])

  const saveConsent = async (preferences) => {
    const nextPreferences = {
      ...preferences,
      [STRICTLY_NECESSARY_FIELD]: true
    }

    setCookie(CONSENT_COOKIE_KEY, JSON.stringify(nextPreferences))
    const deviceKey = getDeviceConsentKey()
    if (deviceKey) localStorage.setItem(deviceKey, JSON.stringify(nextPreferences))
    applyAnalyticsConsent(nextPreferences[ANALYTICS_FIELD] === true)
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: nextPreferences })
    )

    if (consentData?.consent?.length) {
      const payload = consentData.consent.map((c) => ({
        ...c,
        isEnabled:
          c.field === STRICTLY_NECESSARY_FIELD
            ? true
            : (nextPreferences[c.field] ?? c.isEnabled)
      }))
      try {
        await updateConsent({ consent: payload })
      } catch {
        // banner already dismissed locally; PUT failure is non-blocking
      }
    }

    setVisible(false)
    setShowPrefs(false)
  }

  const handleAcceptAll = () => {
    const all = {}
    consentData?.consent?.forEach((c) => { all[c.field] = true })
    saveConsent(all)
  }

  const handleRejectNonEssential = () => {
    const essential = {}
    consentData?.consent?.forEach((c) => {
      essential[c.field] = c.field === STRICTLY_NECESSARY_FIELD
    })
    saveConsent(essential)
  }

  const handleConfirmChoices = () => saveConsent(prefs)

  const togglePref = (field) =>
    setPrefs((p) => ({ ...p, [field]: !p[field] }))

  if (!visible && !showPrefs) return null

  const title = COOKIE_CONSENT.BANNER_TITLE
  const description = consentData?.description || ''
  const panelTitle = consentData?.manage_preferences?.title
  const panelDescription = consentData?.manage_preferences?.description || ''
  const categories = consentData?.consent || []

  return (
    <>
      {showPrefs && (
        <Backdrop onClick={() => setShowPrefs(false)}>
          <PrefsPanel onClick={(e) => e.stopPropagation()}>
            <PanelHeader>
              <BackArrow onClick={() => setShowPrefs(false)}>&#8592;</BackArrow>
              <PanelTitle>{panelTitle}</PanelTitle>
              <CloseButton onClick={() => setShowPrefs(false)}>&#10005;</CloseButton>
            </PanelHeader>
            <ScrollableContent>
              <PanelDescription>{panelDescription}</PanelDescription>
              {categories.map((cat) => (
                <CategoryItem
                  key={cat.field}
                  label={cat.name}
                  field={cat.field}
                  content={cat.desc}
                  isLocked={cat.field === STRICTLY_NECESSARY_FIELD}
                  value={prefs[cat.field] ?? cat.isEnabled}
                  onToggle={togglePref}
                />
              ))}
            </ScrollableContent>
            <ConfirmButtonWrapper>
              <ConfirmButton onClick={handleConfirmChoices}>
                {COOKIE_CONSENT.CONFIRM_MY_CHOICES}
              </ConfirmButton>
            </ConfirmButtonWrapper>
          </PrefsPanel>
        </Backdrop>
      )}
      {visible && (
        <BannerWrapper id="cookie-consent-banner">
          <TextBlock>
            <BannerTitle>{title}</BannerTitle>
            <BannerDescription>
              {buildDescription(description)}
            </BannerDescription>
          </TextBlock>
          <ButtonsContainer>
            <TopButtonRow>
              <AcceptButton onClick={handleAcceptAll}>{COOKIE_CONSENT.ACCEPT_ALL}</AcceptButton>
              <RejectButton onClick={handleRejectNonEssential}>{COOKIE_CONSENT.REJECT_NON_ESSENTIAL}</RejectButton>
            </TopButtonRow>
            <ManageButton onClick={() => setShowPrefs(true)}>{COOKIE_CONSENT.MANAGE_PREFERENCES}</ManageButton>
          </ButtonsContainer>
        </BannerWrapper>
      )}
    </>
  )
}
